/**
 * THE CHART'S SYMBOL, REMEMBERED ACROSS A REMOUNT.
 *
 * <h2>What was wrong</h2>
 *
 * `selectedSymbol` lived in component state with no persistence at all, initialised to
 * {@code appConfig.defaultTicker || 'SPY'}. There are only three places that change it and every
 * one is an explicit gesture — the scan viewer, a scanner row, a Copilot command — so no code path
 * could reset it. What reset it was a REMOUNT, and the symbol silently reverted to the default.
 *
 * <p>In local development that happens whenever Fast Refresh reloads a module (it is enabled
 * explicitly, `FAST_REFRESH=true`), which means editing any file in `src/` while reading a chart
 * throws the reader back to SPY. In production it happens on an ordinary page refresh. Either way
 * the single most important piece of view state was the only one not kept: the toolbar profile, the
 * theme, the glass transparency and the Chart Analytica text scale were all already persisted.
 *
 * <h2>⚠ IT RESTORES BLINDLY AND DOES NOT VALIDATE AGAINST THE SYMBOL LIST</h2>
 *
 * The obvious extra safety — "if the stored symbol is not in the list, fall back to the default" —
 * is refused, for three reasons:
 *
 * <ul>
 *   <li>It would reintroduce the defect. A silent revert to SPY is precisely what is being fixed,
 *       and a validator that performs one is the same surprise wearing a safety jacket.</li>
 *   <li>The list arrives asynchronously, so a validator would run against an empty list on the
 *       first pass and would have to be taught to tell "not in the list" from "no list yet".</li>
 *   <li>A signed-in customer is served their OWN market's list, so a symbol valid for one reader
 *       is absent for another. Falling back would make the restore depend on who is looking.</li>
 * </ul>
 *
 * A symbol that no longer resolves is already handled: the chart's fetch calls `setError` with the
 * message, which names the problem. An error a reader can act on beats a revert they cannot see.
 *
 * <h2>⚠ THE SYNTHETIC `Z-Custom` SYMBOL IS NEVER STORED</h2>
 *
 * An uploaded CSV lives in memory for the session. Storing its symbol would mean every later load
 * restored a chart whose data no longer exists — a guaranteed error on startup, caused by the
 * feature meant to make startup better. {@link isCustomSymbol} is reused rather than comparing the
 * literal, so the two cannot disagree about what counts as custom.
 */

import { isCustomSymbol } from '../constants/customSymbol';

/** The localStorage key. Namespaced like `toolbar_profile` and `product_target_override`. */
export const LAST_SYMBOL_STORAGE_KEY = 'chart_last_symbol';

/**
 * The characters a symbol may contain, matching the backend's own `VALID_SYMBOL`.
 *
 * <p>⚠ A SHAPE CHECK, NOT A MEMBERSHIP CHECK. Its job is to stop a corrupted or hand-edited
 * storage value becoming a request path — `^` is legal in a symbol and has already cost this
 * repository a 400 from Tomcat, and a value with a slash or a newline in it has no business
 * reaching a URL at all. It deliberately says nothing about whether the symbol exists.
 */
const VALID_SYMBOL = /^[A-Za-z0-9.^_-]{1,32}$/;

/** A symbol worth storing: a plausible shape, and not the synthetic upload. */
export const isStorableSymbol = (symbol) => {
  if (typeof symbol !== 'string') return false;
  const clean = symbol.trim();
  return clean.length > 0 && VALID_SYMBOL.test(clean) && !isCustomSymbol(clean);
};

/**
 * The remembered symbol, or null.
 *
 * <p>Null rather than a default, so the caller keeps its own fallback chain
 * (`appConfig.defaultTicker` then `SPY`) in one place instead of this module holding a second
 * opinion about what the default is.
 */
export const readLastSymbol = () => {
  try {
    const stored = window.localStorage.getItem(LAST_SYMBOL_STORAGE_KEY);
    return isStorableSymbol(stored) ? stored.trim() : null;
  } catch (e) {
    // No storage, or storage disabled. The session still works; it just does not remember.
    return null;
  }
};

/** Remembers {@code symbol}, unless it is unstorable — in which case the previous value stands. */
export const writeLastSymbol = (symbol) => {
  if (!isStorableSymbol(symbol)) return;
  try {
    window.localStorage.setItem(LAST_SYMBOL_STORAGE_KEY, String(symbol).trim());
  } catch (e) {
    // A private-mode browser with storage disabled still gets a working chart for the session.
  }
};

export default readLastSymbol;
